import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * SearchableDropdown - A reusable dropdown component with search functionality.
 * Designed to match the look and feel of the custom report builder filters.
 * Now uses React Portal to avoid clipping by modals.
 * 
 * Props:
 * - options: Array of { value, label } or strings
 * - value: Current selected value (or array if isMulti)
 * - onChange: Callback function(value)
 * - placeholder: Placeholder text
 * - isMulti: Boolean, if true allows multiple selections (default: false)
 * - showCheckbox: Boolean, if false hides checkboxes in single-select (default: true)
 * - style: Custom styles for the trigger element
 */
const SearchableDropdown = ({ options = [], value, onChange, placeholder = 'Select...', isMulti = false, showCheckbox = true, style = {}, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);
    const [menuRect, setMenuRect] = useState({ top: 0, left: 0, width: 0 });

    // Normalize options to [{ value, label }]
    const normalizedOptions = options.map(opt => {
        if (typeof opt === 'string' || typeof opt === 'number') {
            return { value: opt, label: String(opt) };
        }
        return opt;
    });

    const filteredOptions = normalizedOptions.filter(opt =>
        String(opt.label).toLowerCase().includes(search.toLowerCase()) ||
        String(opt.value).toLowerCase().includes(search.toLowerCase())
    );

    const updatePosition = () => {
        if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setMenuRect({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useLayoutEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    const handleSelect = (option) => {
        if (isMulti) {
            const selectedArray = Array.isArray(value) ? value : [];
            const nextValue = selectedArray.includes(option.value)
                ? selectedArray.filter(v => v !== option.value)
                : [...selectedArray, option.value];
            onChange(nextValue);
        } else {
            onChange(option.value);
            setIsOpen(false);
            setSearch('');
        }
    };

    const isSelected = (val) => {
        if (isMulti) {
            return Array.isArray(value) && value.includes(val);
        }
        return value === val;
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest('.dropdown-portal-content')) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedLabel = isMulti
        ? (Array.isArray(value) && value.length > 0 ? `${value.length} Selected` : placeholder)
        : (normalizedOptions.find(opt => opt.value === value)?.label || placeholder);

    const dropdownMenu = (
        <div
            className="dropdown-portal-content fade-in"
            style={{
                position: 'absolute',
                top: menuRect.top + 4,
                left: menuRect.left,
                width: menuRect.width,
                background: '#fff',
                border: '1px solid #d2d2d2',
                borderRadius: 8,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                zIndex: 9999,
                maxHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <div style={{ padding: 8, borderBottom: '1px solid #f3f4f6', background: '#fcfcfc' }}>
                <div style={{ position: 'relative' }}>
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9ca3af"
                        strokeWidth="2.5"
                        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}
                    >
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        autoFocus
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px 8px 32px',
                            borderRadius: 6,
                            border: '1px solid #e5e7eb',
                            fontSize: 13,
                            outline: 'none',
                            transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt, idx) => (
                        <div
                            key={opt.value || idx}
                            onClick={() => handleSelect(opt)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '10px 14px',
                                cursor: 'pointer',
                                transition: 'background 0.1s',
                                background: isSelected(opt.value) ? '#e3f2fd' : 'transparent'
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.background = isSelected(opt.value) ? '#e3f2fd' : '#f9fafb')}
                            onMouseOut={(e) => (e.currentTarget.style.background = isSelected(opt.value) ? '#e3f2fd' : 'transparent')}
                        >
                            {(isMulti || showCheckbox) && (
                                <div
                                    style={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: 4,
                                        border: `1.5px solid ${isSelected(opt.value) ? '#1976d2' : '#d2d2d2'}`,
                                        background: isSelected(opt.value) ? '#1976d2' : '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}
                                >
                                    {isSelected(opt.value) && (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4">
                                            <path d="M20 6L9 17l-5-5" />
                                        </svg>
                                    )}
                                </div>
                            )}
                            <span
                                style={{
                                    fontSize: 13,
                                    color: (isSelected(opt.value) && (isMulti || showCheckbox)) ? '#1976d2' : '#374151',
                                    fontWeight: isSelected(opt.value) ? 600 : 400,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {opt.label}
                            </span>
                        </div>
                    ))
                ) : (
                    <div style={{ padding: 16, fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
                        No options found
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: '100%', ...style }}>
            <div
                onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
                style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #d2d2d2',
                    background: disabled ? '#f3f4f6' : '#fff',
                    fontSize: 13,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: 36,
                    color: disabled ? '#9ca3af' : ((value && (!isMulti || (Array.isArray(value) && value.length > 0))) ? '#1a1c1d' : '#9ca3af'),
                    transition: 'all 0.2s',
                    boxShadow: isOpen ? '0 0 0 2px rgba(25, 118, 210, 0.1)' : 'none',
                    borderColor: isOpen ? '#1976d2' : '#d2d2d2',
                    opacity: disabled ? 0.7 : 1
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedLabel}
                </span>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6d7175"
                    strokeWidth="3"
                    style={{
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                        marginLeft: 8,
                        flexShrink: 0
                    }}
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </div>

            {isOpen && createPortal(dropdownMenu, document.body)}
        </div>
    );
};

export default SearchableDropdown;
